/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable import/no-default-export */
import React, { useMemo, useState } from 'react';
import { Box, Flex, Text, Spinner, useDisclosure } from '@chakra-ui/core';
import { Cell, Column, SortingRule } from 'react-table';
import { useTranslation } from 'react-i18next';
import { MdPersonSearch } from 'react-icons/md';
import { CandidateMessagingViewModal } from '../../app/components/CandidateMessagingViewModal';
import { CandidateSearchResponseType } from './CandidateSearchTypes';
import { VirtualTable } from '../../app/components/VirtualTable';
import { FullNameCell } from './cells/FullNameCell';
import { LocationCell } from './cells/LocationCell';
import { PhoneCell } from './cells/PhoneCell';
import { StatusCell } from './cells/StatusCell';
import { LastUpdatedAtCell } from './cells/LastUpdatedAtCell';
import { SeekerHireDetailIdCell } from './cells/SeekerHireDetailIdCell';
import colors from '../../styles/colors';
import Pagination from '../common/Pagination/Pagination';

type SortBy = 'fullName' | 'locationName' | 'statusDetails.status.keyword' | 'statusDetails.lastUpdatedAt';

export type TableProps = {
  data: CandidateSearchResponseType[];
  isLoading?: boolean;
  isFetching?: boolean;
  currentPage: number;
  pageLimit: number;
  totalDataCount: number;
  handlePageChange: (page: number) => void;
  handleItemsPerPageChange: (numItems: number) => void;
  isHireFormEnabled?: boolean;
  sortBy?: SortingRule<CandidateSearchResponseType>[];
  onSort?: (columnId: SortBy) => void;
  disableSortReset?: boolean;
};

export default function CandidateSearchTableView({
  data,
  isLoading,
  isFetching,
  currentPage,
  pageLimit,
  totalDataCount,
  handlePageChange,
  handleItemsPerPageChange,
  isHireFormEnabled,
  sortBy,
  onSort,
  disableSortReset,
}: TableProps) {
  const { t } = useTranslation('candidateSearch');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentCandidateId, setCurrentCandidateId] = useState<string>('');
  const [isMessagingModal, setIsMessagingModal] = useState<boolean>(false);

  // istanbul ignore next
  const onCandidateModalOpen = React.useCallback(
    (candidateId: string) => {
      setCurrentCandidateId(candidateId);
      setIsMessagingModal(false);
      onOpen();
    },
    [onOpen],
  );

  const columns = useMemo<Column<CandidateSearchResponseType>[]>(() => {
    const cols: Column<CandidateSearchResponseType>[] = [
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('tableHeader.candidateName') as string,
        accessor: 'fullName',
        width: 250,
        Cell: function FullNameCellRender({ cell }: { cell: Cell<CandidateSearchResponseType> }) {
          return <FullNameCell cell={cell} onCandidateModalOpen={onCandidateModalOpen} />;
        },
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('tableHeader.location') as string,
        accessor: 'locationName',
        id: 'locationName',
        Cell: function LocationCellRender({ cell }: { cell: Cell<CandidateSearchResponseType> }) {
          return <LocationCell cell={cell} />;
        },
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('tableHeader.phone') as string,
        accessor: 'phone',
        disableSortBy: true,
        Cell: function PhoneCellRender({ cell }: { cell: Cell<CandidateSearchResponseType> }) {
          return <PhoneCell cell={cell} />;
        },
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('tableHeader.status') as string,
        accessor: (row) => row?.statusDetails?.status,
        disableSortBy: true,
        id: 'statusDetails.status.keyword',
        Cell: function StatusCellRender({ cell }: { cell: Cell<CandidateSearchResponseType> }) {
          return <StatusCell cell={cell} />;
        },
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('tableHeader.lastUpdate') as string,
        accessor: (row) => row?.statusDetails?.lastUpdatedAt,
        id: 'statusDetails.lastUpdatedAt',
        Cell: function LastUpdatedAtCellRender({ cell }: { cell: Cell<CandidateSearchResponseType> }) {
          return <LastUpdatedAtCell cell={cell} />;
        },
      },
    ];

    if (isHireFormEnabled) {
      cols.push({
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('tableHeader.hireId') as string,
        accessor: 'seekerHireDetailId',
        disableSortBy: true,
        Cell: function SeekerHireDetailIdCellRender({ cell }: { cell: Cell<CandidateSearchResponseType> }) {
          return <SeekerHireDetailIdCell cell={cell} />;
        },
      });
    }

    return cols;
  }, [t, isHireFormEnabled, onCandidateModalOpen]);

  const handleTableSort = (columnId: string) => {
    onSort?.(columnId as SortBy);
  };

  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" p={5}>
        <Spinner colorScheme="blue" size="lg" data-testid="loading-spinner" />
      </Flex>
    );
  }

  return (
    <Box>
      <VirtualTable
        data={data}
        columns={columns}
        getDataId={(row) => row.id}
        rowHeight={55}
        tableHeight="calc(100vh - 280px)"
        sortBy={sortBy}
        onSort={handleTableSort}
        disableSortReset={disableSortReset}
        pagination={
          <Pagination
            currentPage={currentPage}
            lastPage={Math.ceil(totalDataCount / pageLimit)}
            total={totalDataCount}
            onPageChange={handlePageChange}
            itemsPerPage={pageLimit}
            onItemsPerPageChange={handleItemsPerPageChange}
            isFetching={isFetching || false}
            storageKey="candidateLookupTable"
          />
        }
        emptyTable={
          <Box position="relative" mt={20}>
            <Flex
              direction="column"
              justifyContent="center"
              alignItems="center"
              textAlign="center"
              py={5}
              width="370px"
              mx="auto"
            >
              <Box>
                <MdPersonSearch size={100} color={colors.gray[400]} />
              </Box>
              <Box>
                <Text fontSize="24px" fontWeight={700}>
                  {t('exploreProfiles')}
                </Text>
              </Box>
              <Box mt={2}>
                <Text fontSize="15px" fontWeight={400} color={colors.gray[300]}>
                  {t('enterDetailsText')}
                </Text>
              </Box>
            </Flex>
          </Box>
        }
      />

      {isOpen && (
        /* istanbul ignore next */ <CandidateMessagingViewModal
          isOpen={isOpen}
          onClose={onClose}
          currentCandidateId={currentCandidateId}
          messagingViewModal={isMessagingModal}
        />
      )}
    </Box>
  );
}
